export { cn, type ClassValue } from "./cn";

/** Generate a short, collision-resistant id for client-side entities. */
export function createId(prefix = "id"): string {
  const random = Math.random().toString(36).slice(2, 8);
  const time = Date.now().toString(36);
  return `${prefix}_${time}${random}`;
}

/** Detect whether the app is running inside the Tauri shell vs. a plain browser. */
export function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** Detect the host platform via the user agent (best-effort, cheap). */
export function getPlatform(): "mac" | "windows" | "linux" | "unknown" {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes("mac")) return "mac";
  if (ua.includes("win")) return "windows";
  if (ua.includes("linux")) return "linux";
  return "unknown";
}

/** Format a timestamp for the status bar / logs (HH:MM:SS). */
export function formatTime(date: Date = new Date()): string {
  return date.toLocaleTimeString("en-GB", { hour12: false });
}

/** Debounce a function by the given delay. */
export function debounce<Args extends unknown[]>(
  fn: (...args: Args) => void,
  delay: number,
): (...args: Args) => void {
  let handle: ReturnType<typeof setTimeout> | undefined;
  return (...args: Args) => {
    if (handle) clearTimeout(handle);
    handle = setTimeout(() => fn(...args), delay);
  };
}

/** Filters a list of workspace-tagged records down to the active workspace.
 * Records with `workspaceId: null` (pre-Phase-4 rows never backfilled) stay
 * visible everywhere rather than becoming orphaned in no workspace at all. */
export function filterByWorkspace<T extends { workspaceId: string | null }>(
  items: T[],
  activeWorkspaceId: string,
): T[] {
  return items.filter((item) => item.workspaceId === null || item.workspaceId === activeWorkspaceId);
}

const CHUNK_ERROR_RELOAD_KEY = "chunk-error-reload";

/** True when an error is a failed lazy-chunk fetch (stale hash from a since-replaced deploy). */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i.test(
    message,
  );
}

/**
 * Reload the page once to recover from a stale chunk reference, without
 * looping forever if the reload doesn't actually fix it.
 */
export function reloadOnceForChunkError(): void {
  if (sessionStorage.getItem(CHUNK_ERROR_RELOAD_KEY)) return;
  sessionStorage.setItem(CHUNK_ERROR_RELOAD_KEY, "1");
  window.location.reload();
}

/** Clear the reload guard once the app has booted successfully. */
export function clearChunkErrorFlag(): void {
  sessionStorage.removeItem(CHUNK_ERROR_RELOAD_KEY);
}

/** Format a byte count into a human-readable string. */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / Math.pow(1024, exponent);
  return `${value.toFixed(value < 10 && exponent > 0 ? 1 : 0)} ${units[exponent]}`;
}
